import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { infospyService } from '../services/api';
import '../styles/InfoSpy.css';

/** Intervalo de atualização silenciosa do histórico (status / fila) enquanto a página está aberta */
const HISTORICO_POLL_MS = 5000;

const HistoricoInfoSpy = () => {
  const { isAdminOrDiretor } = useAuth();
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    cpf: '',
    status: '',
    data_inicio: '',
    data_fim: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [liveUpdating, setLiveUpdating] = useState(false);

  const fetchConsultas = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setLoading(true);
    else setLiveUpdating(true);
    try {
      const cleanFilters = {};
      if (filters.cpf) cleanFilters.cpf = filters.cpf.replace(/\D/g, '');
      if (filters.status) cleanFilters.status = filters.status;
      if (filters.data_inicio) cleanFilters.data_inicio = filters.data_inicio;
      if (filters.data_fim) cleanFilters.data_fim = filters.data_fim;

      const response = await infospyService.getHistorico(cleanFilters, page, 20);
      if (response.success) {
        setConsultas(response.data.consultas);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
        if (!silent) setError('');
      }
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.error || 'Erro ao carregar histórico');
      }
    } finally {
      if (!silent) setLoading(false);
      else setLiveUpdating(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchConsultas();
  }, [fetchConsultas]);

  // Atualização periódica ao vivo (aba visível): status e posição na fila sem F5
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      fetchConsultas({ silent: true });
    };
    const id = setInterval(tick, HISTORICO_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchConsultas]);

  // Mantém o modal alinhado aos dados mais recentes do polling
  useEffect(() => {
    if (!showModal) return;
    setSelectedConsulta((prev) => {
      if (!prev) return prev;
      const updated = consultas.find((c) => c.id === prev.id);
      return updated || prev;
    });
  }, [consultas, showModal]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCpfChange = (e) => {
    const value = e.target.value;
    const formatted = value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
    setFilters(prev => ({ ...prev, cpf: formatted }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchConsultas();
  };

  const handleClearFilters = () => {
    setFilters({
      cpf: '',
      status: '',
      data_inicio: '',
      data_fim: ''
    });
    setPage(1);
  };

  const handleViewConsulta = async (consulta) => {
    setSelectedConsulta(consulta);
    setShowModal(true);
  };

  const handleDeleteConsulta = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta consulta? As imagens associadas também serão removidas.')) return;

    try {
      await infospyService.deleteConsulta(id);
      setConsultas(prev => prev.filter(c => c.id !== id));
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir consulta');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Aguardando', icon: '⏳', class: 'infospy-status-pending' },
      processing: { label: 'Processando', icon: '🔄', class: 'infospy-status-processing' },
      completed: { label: 'Concluído', icon: '✅', class: 'infospy-status-completed' },
      sem_cadastro: { label: 'Sem Cadastro', icon: '⚠️', class: 'infospy-status-sem_cadastro' },
      error: { label: 'Erro', icon: '❌', class: 'infospy-status-error' }
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  /** Posição na fila externa (só relevante enquanto aguarda ou processa) */
  const formatPosicaoFila = (consulta) => {
    if (!['pending', 'processing'].includes(consulta.status)) return '—';
    if (consulta.posicao_fila != null && consulta.posicao_fila !== '') {
      return `#${consulta.posicao_fila}`;
    }
    return '…';
  };

  return (
    <Layout>
      <div className="infospy-container">
        {/* Header */}
        <div className="infospy-header">
          <div className="infospy-header-left">
            <h1 className="infospy-title">
              <div className="infospy-title-icon">📊</div>
              Histórico de Consultas
            </h1>
            <p className="infospy-subtitle">
              {isAdminOrDiretor()
                ? 'Visualize e gerencie todas as consultas realizadas'
                : 'Visualize as consultas do seu usuário e, se for supervisor, da sua equipe'}
            </p>
          </div>
          <div className="infospy-header-right">
            <button
              className="infospy-btn infospy-btn-primary"
              onClick={() => window.location.href = '/infospy'}
            >
              🔍 Nova Consulta
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="infospy-card">
          <div className="infospy-card-header">
            <div className="infospy-card-header-icon">🔍</div>
            <span className="infospy-card-header-title">Filtros</span>
          </div>
          <div className="infospy-card-body">
            <form onSubmit={handleFilterSubmit}>
              <div className="infospy-form" style={{ flexWrap: 'wrap' }}>
                <div className="infospy-form-group" style={{ minWidth: '180px' }}>
                  <label className="infospy-form-label">CPF</label>
                  <input
                    type="text"
                    name="cpf"
                    value={filters.cpf}
                    onChange={handleCpfChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className="infospy-form-input"
                  />
                </div>

                <div className="infospy-form-group" style={{ minWidth: '150px' }}>
                  <label className="infospy-form-label">Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="infospy-form-input"
                  >
                    <option value="">Todos</option>
                    <option value="pending">Aguardando</option>
                    <option value="processing">Processando</option>
                    <option value="completed">Concluído</option>
                    <option value="sem_cadastro">Sem Cadastro</option>
                    <option value="error">Erro</option>
                  </select>
                </div>

                <div className="infospy-form-group" style={{ minWidth: '150px' }}>
                  <label className="infospy-form-label">Data Início</label>
                  <input
                    type="date"
                    name="data_inicio"
                    value={filters.data_inicio}
                    onChange={handleFilterChange}
                    className="infospy-form-input"
                  />
                </div>

                <div className="infospy-form-group" style={{ minWidth: '150px' }}>
                  <label className="infospy-form-label">Data Fim</label>
                  <input
                    type="date"
                    name="data_fim"
                    value={filters.data_fim}
                    onChange={handleFilterChange}
                    className="infospy-form-input"
                  />
                </div>

                <button type="submit" className="infospy-btn infospy-btn-primary">
                  🔍 Filtrar
                </button>
                <button type="button" onClick={handleClearFilters} className="infospy-btn infospy-btn-secondary">
                  ✖️ Limpar
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="infospy-error">
            <span className="infospy-error-icon">❌</span>
            <span>{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="infospy-card">
          <div className="infospy-card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className="infospy-card-header-icon">📋</div>
              <span className="infospy-card-header-title">Consultas ({total})</span>
            </div>
            <div
              className="infospy-queue-updating"
              style={{ marginLeft: 'auto', opacity: liveUpdating ? 1 : 0.65 }}
              title={`Atualização automática a cada ${HISTORICO_POLL_MS / 1000}s com a página aberta`}
            >
              <div className="infospy-pulse-dot" />
              <span style={{ fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                {liveUpdating ? 'Atualizando…' : 'Ao vivo'}
              </span>
            </div>
          </div>
          <div className="infospy-card-body" style={{ padding: 0 }}>
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--texto-secundario)' }}>
                <div className="infospy-spinner" style={{ margin: '0 auto 1rem', width: '32px', height: '32px', borderWidth: '3px' }}></div>
                Carregando...
              </div>
            ) : consultas.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--texto-secundario)' }}>
                <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📭</p>
                <p>Nenhuma consulta encontrada</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--cinza-escuro-2)' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ID</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CPF</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Usuário</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fila</th>
                    <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Imagens</th>
                    <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Data</th>
                    <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid var(--border-color)', color: 'var(--texto-secundario)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consultas.map((consulta) => (
                    <tr key={consulta.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '1rem', color: 'var(--texto-primario)', fontFamily: 'monospace' }}>#{consulta.id}</td>
                      <td style={{ padding: '1rem', color: 'var(--texto-primario)', fontFamily: 'monospace', fontWeight: '500' }}>
                        {formatCPF(consulta.cpf)}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ color: 'var(--texto-primario)', fontWeight: '500' }}>
                          {consulta.usuario?.nome || 'N/A'}
                        </div>
                        <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem' }}>
                          {consulta.usuario?.username}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span className={`infospy-status ${getStatusConfig(consulta.status).class}`}>
                          {getStatusConfig(consulta.status).icon} {getStatusConfig(consulta.status).label}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center', fontFamily: 'monospace', fontWeight: '600', color: 'var(--texto-primario)' }}>
                        {formatPosicaoFila(consulta)}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <span style={{ 
                          background: consulta.imagens?.length > 0 ? 'rgba(0,255,136,0.15)' : 'rgba(255,170,0,0.15)',
                          color: consulta.imagens?.length > 0 ? '#00ff88' : '#ffaa00',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          📷 {consulta.imagens ? [...new Set(consulta.imagens.map(img => img.filename))].length : 0}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                        {formatDate(consulta.created_at)}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={() => handleViewConsulta(consulta)}
                          className="infospy-btn infospy-btn-secondary"
                          style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                        >
                          👁️ Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '1rem 1.5rem', 
                borderTop: '1px solid var(--border-color)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                  Total: {total} consultas
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="infospy-btn infospy-btn-secondary"
                    style={{ padding: '0.5rem 1rem', opacity: page === 1 ? 0.5 : 1 }}
                  >
                    ◀ Anterior
                  </button>
                  <span style={{ color: 'var(--texto-primario)', fontWeight: '500' }}>
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="infospy-btn infospy-btn-secondary"
                    style={{ padding: '0.5rem 1rem', opacity: page === totalPages ? 0.5 : 1 }}
                  >
                    Próximo ▶
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedConsulta && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" style={{ maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span>{getStatusConfig(selectedConsulta.status).icon}</span>
                Detalhes da Consulta #{selectedConsulta.id}
              </h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>CPF</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: '600', color: 'var(--texto-primario)' }}>
                    {formatCPF(selectedConsulta.cpf)}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Status</div>
                  <span className={`infospy-status ${getStatusConfig(selectedConsulta.status).class}`}>
                    {getStatusConfig(selectedConsulta.status).icon} {getStatusConfig(selectedConsulta.status).label}
                  </span>
                  {['pending', 'processing'].includes(selectedConsulta.status) && (
                    <div style={{ marginTop: '0.6rem', color: 'var(--texto-secundario)', fontSize: '0.85rem' }}>
                      Posição na fila:{' '}
                      <span style={{ fontFamily: 'monospace', fontWeight: '700', color: 'var(--texto-primario)' }}>
                        {formatPosicaoFila(selectedConsulta)}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Usuário</div>
                  <div style={{ color: 'var(--texto-primario)', fontWeight: '500' }}>
                    {selectedConsulta.usuario?.nome || 'N/A'}
                    <span style={{ color: 'var(--texto-secundario)', fontWeight: '400', marginLeft: '0.5rem' }}>
                      ({selectedConsulta.usuario?.username})
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--texto-secundario)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Data/Hora</div>
                  <div style={{ color: 'var(--texto-primario)' }}>{formatDate(selectedConsulta.created_at)}</div>
                </div>
              </div>

              {selectedConsulta.error_message && (
                <div className="infospy-error" style={{ marginBottom: '1rem' }}>
                  <span className="infospy-error-icon">❌</span>
                  <span>{selectedConsulta.error_message}</span>
                </div>
              )}

              {selectedConsulta.status === 'completed' && selectedConsulta.imagens?.length > 0 && (() => {
                const uniqueImages = selectedConsulta.imagens.filter((img, index, self) =>
                  index === self.findIndex(i => i.filename === img.filename)
                );
                return (
                  <div style={{ marginBottom: '1rem' }}>
                    <h3 style={{ color: 'var(--texto-primario)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      📷 Imagens ({uniqueImages.length})
                    </h3>
                    <div className="infospy-images-grid">
                      {uniqueImages.map((img) => (
                        <div 
                          key={img.id} 
                          className="infospy-image-card"
                          onClick={() => setSelectedImage({ 
                            url: infospyService.getImageUrl(selectedConsulta.id, img.filename), 
                            filename: img.filename 
                          })}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="infospy-image-wrapper">
                            <img
                              src={infospyService.getImageUrl(selectedConsulta.id, img.filename)}
                              alt={img.filename}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM6Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzFhMWFhYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW0gbsOjbyBlbmNvbnRyYWRhPC90ZXh0Pjwvc3ZnPg==';
                              }}
                            />
                            <div className="infospy-image-overlay">
                              <span className="infospy-image-overlay-text">🔍 Clique para ampliar</span>
                            </div>
                          </div>
                          <div className="infospy-image-name">{img.filename}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button onClick={() => setShowModal(false)} className="infospy-btn infospy-btn-secondary">
                  ✕ Fechar
                </button>
                {isAdminOrDiretor() && (
                  <button 
                    onClick={() => handleDeleteConsulta(selectedConsulta.id)} 
                    className="infospy-btn"
                    style={{ background: '#dc2626', color: 'white' }}
                  >
                    🗑️ Excluir
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="infospy-modal-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="infospy-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="infospy-modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.filename}
              className="infospy-modal-image"
            />
            <div className="infospy-modal-filename">{selectedImage.filename}</div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default HistoricoInfoSpy;